import { createSlice, createSelector, createEntityAdapter, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { sortByFieldAlphabetically } from "@amplio-common/funcs/commonFunctionalities";
import { simpleSetPayload, VIDEO_LOG_INTERVAL_TIME } from "../constants";
import { updateLogVideo } from "@amplio-common/treatments/api";
import * as workerTimers from "worker-timers";
import { selectTreatmentId } from "./commonSelectorsV2";
import { SLICE_NAMES } from "./sliceNames";

export const askToJoinVideo = createAction(`${SLICE_NAMES.VIDEO_SLICE}/askToJoin`);

//define root selector
const videoSlice_STATE_ROOT_SELECTOR = state => state[SLICE_NAMES.VIDEO_SLICE];

const simpleFieldSelector = (fieldName) => createSelector(
  [state => state[SLICE_NAMES.VIDEO_SLICE][fieldName]], field => field,
);

const sortFunc = sortByFieldAlphabetically("name");

export const videoStudentsAdapter = createEntityAdapter({
  sortComparer: sortFunc,
});

export const videoMapTSToStudentStatus = (ts) => ({
  id: ts.student.id,
  name: ts.student.name,
  isVideoMuted: false,
  isVideoPaused: false,
  isExitVideo: false,
  isKickedFromVideo: false,
  audioDeviceId: null,
  isVoiceSettingsOpen: false,
  videoDevice: null,
  
});
  
const videoStudentsAdapterSelectors = videoStudentsAdapter.getSelectors(videoSlice_STATE_ROOT_SELECTOR);

const initialStateUser = {
  isVideoFullScreen: false,
  isVideoMuted: false,
  isVideoPaused: false,
  isOpentokEnabled: true,
  isExitVideo: false,
  isOpentokError: false,
  opentokErrorMessage: null,
  isKickedFromVideo: false,
  isShareScreenOn: false,
  isShareScreenError: false,
  isPublishSuccess: false,
  opentokReconnect: false,
  streamUsersIds: [],
  timeLogInterval: null,
  logId: null,
  isVideoAllowed: true,
};

const initialState = {
  ...initialStateUser,
  ...videoStudentsAdapter.getInitialState(),
};

export const userStartVideo = createAsyncThunk(
  `${SLICE_NAMES.VIDEO_SLICE}/userStartVideo`,
  async (payload, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState();
    const treatmentId = selectTreatmentId(state);
    const res = await updateLogVideo(treatmentId);

    return { res, treatmentId };
  },
);

export const clearUserVideo = createAsyncThunk(
  `${SLICE_NAMES.VIDEO_SLICE}/clearUserVideo`,
  async (payload, thunkAPI) => {
    const { getState } = thunkAPI;
    const state = getState();
    // eslint-disable-next-line no-use-before-define
    const logId = selectLogId(state);
    const treatmentId = selectTreatmentId(state);
    logId && treatmentId && updateLogVideo(treatmentId, { log_id: logId });
  },
);

export const videoSlice = createSlice({
  name: SLICE_NAMES.VIDEO_SLICE,
  initialState,
  reducers: {
    clearVideo: () => ({ ...initialState }),
    userDeclineVideo: (state) => ({ ...state,
      ...initialStateUser,
      isOpentokError: false,
      isOpentokEnabled: state.isOpentokEnabled,
    }),
    endVideoSession: (state) => ({ ...state,
      isExitVideo: true,
    }),
    setIsVideoMuted: simpleSetPayload("isVideoMuted"),
    setIsVideoFullscreen: simpleSetPayload("isVideoFullScreen"),
    setIsOpentokEnabled: simpleSetPayload("isOpentokEnabled"),
    setIsVideoPaused: simpleSetPayload("isVideoPaused"),
    setIsVideoPIP: simpleSetPayload("isVideoPIP"),
    setIsExitVideo: simpleSetPayload("isExitVideo"),
    setIsOpentokError: simpleSetPayload("isOpentokError"),
    setOpentokErrorMessage: simpleSetPayload("opentokErrorMessage"),
    setIsKickedFromVideo: simpleSetPayload("isKickedFromVideo"),
    setIsShareScreenError: simpleSetPayload("isShareScreenError"),
    setIsPublishSuccess: simpleSetPayload("isPublishSuccess"),
    setOpentokReconnect: simpleSetPayload("opentokReconnect"),
    setStreamUsersIds: simpleSetPayload("streamUsersIds"),
    setIsVideoAllowed: simpleSetPayload("isVideoAllowed"),
    setAudioDeviceId: simpleSetPayload("audioDeviceId"),
    setIsVoiceSettingsOpen: simpleSetPayload("isVoiceSettingsOpen"),
    setVideoDevice: simpleSetPayload("videoDevice"),
    pushToStreamUsersIds: (state, action) => {
      const ids = state.streamUsersIds;
      if (ids.includes(action.payload)) {
        return;
      }
      state.streamUsersIds = [...ids, action.payload];
    },
    videoUpdateStudentStatus: videoStudentsAdapter.updateOne,
    videoSetStudentsStatus: videoStudentsAdapter.setAll,
    videoUpdateAllStudents: videoStudentsAdapter.updateMany,
  },
  extraReducers: {
    [userStartVideo.fulfilled]: (state, action) => {
      const { treatmentId, res } = action.payload;
      const { log_id } = res.response;

      return ({ ...state,
        isVideoMuted: false,
        timeLogInterval: workerTimers.setInterval(() => updateLogVideo(treatmentId, { log_id }), VIDEO_LOG_INTERVAL_TIME),
        logId: log_id,
      });
    },
    [clearUserVideo.fulfilled]: (state) => {
      state.timeLogInterval && workerTimers.clearInterval(state.timeLogInterval);
      return ({ ...state,
        ...initialStateUser,
        streamUsersIds: state.streamUsersIds,
        isShareScreenOn: state.isShareScreenOn,
        // isOpentokEnabled: state.isOpentokEnabled,
      });
    },
  },
});

export default videoSlice.reducer;

//actions
export const { videoUpdateStudentStatus,
  videoSetStudentsStatus, videoUpdateAllStudents, clearVideo, setIsOpentokEnabled, setIsVideoPIP,
  setIsVideoPausedByTeacher, setIsVideoPaused,
  setIsVideoMuted, setIsTeacherVideoMuted,
  setOpentokErrorMessage, setIsExitVideo, setIsOpentokError, setIsKickedFromVideo,
  setIsShareScreenError, userDeclineVideo, setAudioDeviceId,
  setIsVoiceSettingsOpen, setVideoDevice, setIsVideoAllowed,
  setIsPublishSuccess, setOpentokReconnect, setStreamUsersIds, pushToStreamUsersIds, endVideoSession, setIsVideoFullscreen } = videoSlice.actions;

//selectors
export const selectIsVideoFullScreen = simpleFieldSelector("isVideoFullScreen");
export const selectIsVideoMuted = simpleFieldSelector("isVideoMuted");
export const selectIsOpentokEnabled = simpleFieldSelector("isOpentokEnabled");
export const selectIsVideoPaused = simpleFieldSelector("isVideoPaused");
export const selectIsVideoPIP = simpleFieldSelector("isVideoPIP");
export const selectIsExitVideo = simpleFieldSelector("isExitVideo");
export const selectIsOpentokError = simpleFieldSelector("isOpentokError");
export const selectOpentokErrorMessage = simpleFieldSelector("opentokErrorMessage");
export const selectIsKickedFromVideo = simpleFieldSelector("isKickedFromVideo");
export const selectIsShareScreenError = simpleFieldSelector("isShareScreenError");
export const selectIsPublishSuccess = simpleFieldSelector("isPublishSuccess");
export const selectOpentokReconnect = simpleFieldSelector("opentokReconnect");
export const selectStreamUsersIds = simpleFieldSelector("streamUsersIds");
export const selectLogId = simpleFieldSelector("logId");
export const selectAudioDeviceId = simpleFieldSelector("audioDeviceId");
export const selectIsVoiceSettingsOpen = simpleFieldSelector("isVoiceSettingsOpen");
export const selectVideoDevice = simpleFieldSelector("videoDevice");
export const selectIsVideoAllowed = simpleFieldSelector("isVideoAllowed");

export const { selectAll: videoSelectAllStudentsStatus,
  selectIds: videoSelectStudentStatusIds, selectById: videoSelectStudentStatusById } = videoStudentsAdapterSelectors;

export const clearStudentVideo = createAsyncThunk(
  `${SLICE_NAMES.VIDEO_SLICE}/clearStudentVideo`,
  async (payload, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const { studentId, newStudentList } = payload;


    dispatch(videoUpdateStudentStatus({ id: studentId,
      changes: {
        isVideoMuted: false,
        isVideoPaused: false,
        isExitVideo: false,
      },
      studentIds: newStudentList,
    }));
  },
);

export const clearAllStudentVideo = createAsyncThunk(
  `${SLICE_NAMES.VIDEO_SLICE}/clearAllStudentVideo`,
  async (_, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;
    const state = getState();
    const studentIds = videoSelectStudentStatusIds(state);
    
    dispatch(videoUpdateAllStudents(studentIds.map(id => ({
      id,
      changes: { isExitVideo: true } }))));

    dispatch(videoUpdateAllStudents(studentIds.map(id => ({
      id,
      changes: {
        isVideoMuted: false,
        isVideoPaused: false,
        isExitVideo: false,
      },
    }))));
  },
);

export const selectIsVideoErrorDialogOpen = createSelector(
  [selectIsOpentokError, selectIsOpentokEnabled],
  (isOpentokError, isOpentokEnabled) => isOpentokError || !isOpentokEnabled,
);
